import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  keyframes,
  LinearProgress,
  Alert,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import StarIcon from '@mui/icons-material/Star';
import { useOptimization } from '../../context/OptimizationContext';
import { calculateCombinedWinner } from '../../utils/combinedWinnerUtils';

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
`;

const flashBest = keyframes`
  0%, 100% {
    background-color: rgba(76, 175, 80, 0.1);
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.4);
  }
  50% {
    background-color: rgba(76, 175, 80, 0.3);
    box-shadow: 0 0 12px 6px rgba(76, 175, 80, 0.6);
  }
`;

const CombinedWinnerLeaderboard: React.FC = () => {
  const { results, isRunning, completedAlgorithms } = useOptimization();

  const combinedWinnerData = useMemo(() => {
    if (!results) return null;

    // Use the utility function to calculate combined winner (handles statistics correctly)
    const calculatedData = calculateCombinedWinner(results);
    if (!calculatedData) return null;

    // Map the calculated data and add isCompleted status for UI purposes
    const rankedAlgorithmsWithStatus = calculatedData.rankedAlgorithms.map(alg => ({
      ...alg,
      isCompleted: completedAlgorithms.has(alg.name),
    }));

    const perfectWinnersWithStatus = calculatedData.perfectWinners.map(winner => ({
      ...winner,
      isCompleted: completedAlgorithms.has(winner.name),
    }));

    const bestAlternativeWithStatus = calculatedData.bestAlternative ? {
      ...calculatedData.bestAlternative,
      isCompleted: completedAlgorithms.has(calculatedData.bestAlternative.name),
    } : null;

    return {
      ...calculatedData,
      perfectWinners: perfectWinnersWithStatus,
      bestAlternative: bestAlternativeWithStatus,
      rankedAlgorithms: rankedAlgorithmsWithStatus,
    };
  }, [results, completedAlgorithms]);

  // Don't show if no results and not running
  if (!combinedWinnerData || combinedWinnerData.rankedAlgorithms.length === 0) {
    if (!isRunning) {
      return null;
    }
    // Show placeholder while running but no results yet
    return (
      <Paper
        elevation={2}
        sx={{
          p: 2,
          mt: 2,
          alignSelf: 'flex-start',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <StarIcon sx={{ mr: 1, color: 'warning.main' }} />
          <Typography variant="h6" component="h2">
            Combined Winner
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
          Waiting for algorithms to complete...
        </Typography>
        <LinearProgress sx={{ mt: 2 }} />
      </Paper>
    );
  }

  const hasMultipleResults = combinedWinnerData.rankedAlgorithms.length > 1;
  const hasPerfectWinner = combinedWinnerData.hasPerfectWinner;

  return (
    <Paper
      elevation={2}
      sx={{
        p: { xs: 1.5, md: 1.5 },
        mt: 2,
        alignSelf: 'flex-start',
        minWidth: { xs: '100%', md: 200 },
        maxWidth: { xs: '100%', md: 250 },
        width: { xs: '100%', md: 'auto' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        <StarIcon sx={{ mr: 0.5, color: 'warning.main', fontSize: 20 }} />
        <Typography variant="subtitle1" component="h2" sx={{ fontSize: '0.95rem', fontWeight: 600 }}>
          Combined Winner
        </Typography>
      </Box>

      {/* Info Alert */}
      <Alert 
        severity="info" 
        sx={{ 
          mb: 1.5, 
          fontSize: '0.7rem',
          py: 0.5,
          '& .MuiAlert-message': {
            fontSize: '0.7rem',
          }
        }}
      >
        Best Solution (Fitness) + Lowest Energy
      </Alert>

      {/* Perfect Winner or Best Alternative Highlight */}
      {hasPerfectWinner && combinedWinnerData.perfectWinners.length > 0 ? (
        // Show perfect winner(s)
        combinedWinnerData.perfectWinners.map((winner) => (
          <Box
            key={winner.key}
            sx={{
              mb: 1.5,
              p: 1.5,
              borderRadius: 1.5,
              backgroundColor: 'rgba(76, 175, 80, 0.1)',
              border: '1.5px solid',
              borderColor: 'success.main',
              animation: `${flashBest} 2s ease-in-out infinite`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <EmojiEventsIcon
                sx={{
                  color: 'success.main',
                  mr: 0.5,
                  fontSize: 20,
                  animation: `${pulse} 1.5s ease-in-out infinite`,
                }}
              />
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 700, 
                  color: 'success.main', 
                  fontSize: '0.85rem' 
                }}
              >
                Perfect Winner: {winner.name}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 600, 
                  color: 'success.main', 
                  fontFamily: 'monospace', 
                  fontSize: '0.8rem' 
                }}
              >
                Fitness: {winner.fitness.toFixed(6)} (#1 - Best Solution)
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 600, 
                  color: 'success.main', 
                  fontFamily: 'monospace', 
                  fontSize: '0.8rem' 
                }}
              >
                Energy: {winner.energy.toFixed(6)} J (#1 - Lowest Energy)
              </Typography>
            </Box>
            <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 0.5, fontSize: '0.65rem', fontWeight: 600 }}>
              ✓ Best solution AND lowest energy
            </Typography>
          </Box>
        ))
      ) : combinedWinnerData.bestAlternative ? (
        // Show best alternative
        <Box
          sx={{
            mb: 1.5,
            p: 1.5,
            borderRadius: 1.5,
            backgroundColor: 'rgba(255, 193, 7, 0.1)',
            border: '1.5px solid',
            borderColor: 'warning.main',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <EmojiEventsIcon
              sx={{
                color: 'warning.main',
                mr: 0.5,
                fontSize: 20,
              }}
            />
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 700, 
                color: 'warning.main', 
                fontSize: '0.85rem' 
              }}
            >
              Best Alternative: {combinedWinnerData.bestAlternative.name}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 600, 
                color: 'text.primary', 
                fontFamily: 'monospace', 
                fontSize: '0.8rem' 
              }}
            >
              Fitness: {combinedWinnerData.bestAlternative.fitness.toFixed(6)} 
              {combinedWinnerData.bestAlternative.fitness === combinedWinnerData.bestFitness 
                ? ' (#1 - Best Solution)' 
                : ` (Rank #${combinedWinnerData.rankedAlgorithms.find(a => a.key === combinedWinnerData.bestAlternative!.key)?.fitnessRank || '?'})`}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 600, 
                color: 'text.primary', 
                fontFamily: 'monospace', 
                fontSize: '0.8rem' 
              }}
            >
              Energy: {combinedWinnerData.bestAlternative.energy.toFixed(6)} J
              {combinedWinnerData.bestAlternative.energy === combinedWinnerData.bestEnergy 
                ? ' (#1 - Lowest Energy)' 
                : ` (Rank #${combinedWinnerData.rankedAlgorithms.find(a => a.key === combinedWinnerData.bestAlternative!.key)?.energyRank || '?'})`}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.65rem' }}>
            No algorithm wins both metrics. This is the closest match.
          </Typography>
        </Box>
      ) : null}

      {/* Leaderboard List */}
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, fontWeight: 600, fontSize: '0.75rem' }}>
          All Algorithms (Sorted by Best Combined Performance)
        </Typography>
        <List dense sx={{ p: 0 }}>
          {combinedWinnerData.rankedAlgorithms.map((entry, index) => {
            const isBest = index === 0;
            const isWorst = index === combinedWinnerData.rankedAlgorithms.length - 1 && hasMultipleResults;
            const rank = index + 1;
            const isPerfect = entry.isPerfectWinner;

            return (
              <ListItem
                key={entry.key}
                sx={{
                  mb: 0.5,
                  borderRadius: 1,
                  backgroundColor: isPerfect
                    ? 'rgba(76, 175, 80, 0.08)'
                    : isBest
                    ? 'rgba(255, 193, 7, 0.08)'
                    : isWorst
                    ? 'rgba(211, 47, 47, 0.08)'
                    : 'transparent',
                  border: isPerfect || isBest ? '1px solid' : 'none',
                  borderColor: isPerfect ? 'success.main' : isBest ? 'warning.main' : 'transparent',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: isPerfect
                      ? 'rgba(76, 175, 80, 0.15)'
                      : isBest
                      ? 'rgba(255, 193, 7, 0.15)'
                      : isWorst
                      ? 'rgba(211, 47, 47, 0.15)'
                      : 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <Chip
                    label={rank}
                    size="small"
                    color={isPerfect ? 'success' : isBest ? 'warning' : isWorst ? 'error' : 'default'}
                    sx={{
                      fontWeight: 700,
                      minWidth: 24,
                      height: 20,
                      fontSize: '0.7rem',
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: isPerfect || isBest ? 700 : isWorst ? 600 : 500,
                          color: isPerfect ? 'success.main' : isBest ? 'warning.main' : isWorst ? 'error.main' : 'text.primary',
                        }}
                      >
                        {entry.name}
                      </Typography>
                      {isPerfect && (
                        <Chip
                          label="Perfect"
                          size="small"
                          color="success"
                          sx={{ height: 16, fontSize: '0.6rem', px: 0.5 }}
                        />
                      )}
                      {!entry.isCompleted && isRunning && (
                        <Chip
                          label="Running"
                          size="small"
                          color="primary"
                          sx={{ height: 16, fontSize: '0.6rem', px: 0.5 }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: 'monospace',
                          color: isPerfect ? 'success.main' : isBest ? 'warning.main' : isWorst ? 'error.main' : 'text.secondary',
                          fontWeight: isPerfect || isBest ? 600 : 400,
                          fontSize: '0.7rem',
                          display: 'block',
                        }}
                      >
                        F: {entry.fitness.toFixed(4)} (#{entry.fitnessRank})
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: 'monospace',
                          color: isPerfect ? 'success.main' : isBest ? 'warning.main' : isWorst ? 'error.main' : 'text.secondary',
                          fontWeight: isPerfect || isBest ? 600 : 400,
                          fontSize: '0.7rem',
                          display: 'block',
                        }}
                      >
                        E: {entry.energy.toFixed(4)} J (#{entry.energyRank})
                      </Typography>
                      {isPerfect && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'success.main',
                            fontSize: '0.65rem',
                            display: 'block',
                            mt: 0.25,
                            fontWeight: 600,
                          }}
                        >
                          ✓ Wins both metrics
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* Progress indicator if still running */}
      {isRunning && combinedWinnerData.rankedAlgorithms.length < 6 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {combinedWinnerData.rankedAlgorithms.length} of 6 algorithms completed
          </Typography>
          <LinearProgress
            variant="determinate"
            value={(combinedWinnerData.rankedAlgorithms.length / 6) * 100}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>
      )}
    </Paper>
  );
};

export default CombinedWinnerLeaderboard;

